import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { IconeAlerte } from '@journal/ui';
import { useSession } from '../lib/session.tsx';

/**
 * Plus de comptes ouverts que de sièges payés.
 *
 * ── Comment on en arrive là ─────────────────────────────────────────────────
 * La direction réduit le nombre de sièges depuis le portail de paiement, pour
 * payer moins, sans retirer personne de l'équipe.
 *
 * ── Ce qui se passe ensuite ─────────────────────────────────────────────────
 * Personne n'est coupé tout de suite : la direction a jusqu'au renouvellement
 * suivant pour retirer des comptes ou reprendre des sièges. Passé cette date,
 * les comptes les plus récents sont en LECTURE SEULE — ils gardent l'accès à
 * leurs préparations, sans pouvoir les modifier.
 *
 * ── À qui ───────────────────────────────────────────────────────────────────
 * À la direction, qui peut agir. Et à l'enseignant dont le compte est en trop,
 * qui doit savoir pourquoi il ne peut plus écrire — sans quoi il croirait à
 * une panne. Les autres enseignants n'y peuvent rien : on ne les inquiète pas.
 */

export interface DepassementSieges {
  comptes: number;
  sieges: number;
  echeance: string | null;
  compteEnTrop: boolean;
  echu: boolean;
}

const jour = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** Ce que dit le bandeau, ou `null` s'il n'a rien à dire à cette personne. */
export function messageSieges(
  d: DepassementSieges | null | undefined,
  direction: boolean,
): { titre: string; suite: string; actions: boolean } | null {
  if (!d) return null;
  const surplus = d.comptes - d.sieges;
  const titre = `${d.comptes} comptes ouverts pour ${d.sieges} sièges payés.`;

  if (direction) {
    return {
      titre,
      suite: d.echu
        ? ` ${surplus} compte${surplus > 1 ? 's sont' : ' est'} en lecture seule depuis le renouvellement.`
        : d.echeance
          ? ` À partir du ${jour(d.echeance)}, le${surplus > 1 ? 's' : ''} ${surplus} compte${surplus > 1 ? 's' : ''} le${surplus > 1 ? 's' : ''} plus récent${surplus > 1 ? 's' : ''} passer${surplus > 1 ? 'ont' : 'a'} en lecture seule.`
          : ' Aucun compte ne peut être ajouté.',
      actions: true,
    };
  }
  if (d.compteEnTrop) {
    return {
      titre: d.echu ? 'Votre compte est en lecture seule.' : 'Votre compte va passer en lecture seule.',
      suite: d.echu
        ? ' Votre école a plus de comptes que de sièges payés : vos données restent consultables, sans modification possible. Voyez avec la direction.'
        : ` Votre école a plus de comptes que de sièges payés${d.echeance ? ` : à partir du ${jour(d.echeance)}, vous ne pourrez plus modifier vos données` : ''}. Voyez avec la direction.`,
      actions: false,
    };
  }
  return null;
}

export function BandeauSieges({ direction }: { direction: boolean }) {
  const { api, etat } = useSession();
  const [depassement, setDepassement] = useState<DepassementSieges | null>(null);

  useEffect(() => {
    if (etat !== 'connecte') {
      setDepassement(null);
      return undefined;
    }
    let vivant = true;
    api.requete<{ seatsOverage: DepassementSieges | null }>('/billing/subscription')
      .then((a) => { if (vivant) setDepassement(a.seatsOverage ?? null); })
      // Un échec ne dit rien : ce bandeau est un rappel, pas une alarme.
      .catch(() => { if (vivant) setDepassement(null); });
    return () => { vivant = false; };
  }, [api, etat]);

  const message = messageSieges(depassement, direction);
  if (!message || !depassement) return null;
  const surplus = depassement.comptes - depassement.sieges;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--color-warning)] bg-[var(--color-warning-soft)] px-4 py-2 text-[13px] print:hidden"
    >
      <IconeAlerte taille={16} />
      <span>
        <strong>{message.titre}</strong>
        {message.suite}
        {message.actions && (
          <>
            {' '}
            <Link to="/equipe" className="underline">
              {`Retirez ${surplus} compte${surplus > 1 ? 's' : ''}`}
            </Link>
            {' ou '}
            <Link to="/abonnement" className="underline">
              {`reprenez ${surplus} siège${surplus > 1 ? 's' : ''}`}
            </Link>
            .
          </>
        )}
      </span>
    </div>
  );
}
